import { useMemo } from 'react';
import { Platform } from 'react-native';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { checkIfAwsWafNativeSdkAvailable } from '@order/modules-aws-waf';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The AWS WAF token source for the current platform, app build and feature flags.
 */
export const useAwsWafTokenSource = () => {
  const isAwsWafEnabled = useFeatureFlag('CELS-5071-aws-waf-enabled');
  const isNativeSdkEnabled = useFeatureFlag('CELS-6184-aws-waf-native-sdk-enabled');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const tokenSource = useMemo<AwsWafTokenSource>(() => {
    if (!isAwsWafEnabled) {
      return 'disabled';
    }

    if (Platform.OS === 'web') {
      return 'web-script';
    }

    // Binaries built before the native module existed keep using the WebView.
    const canUseNativeSdk = isNativeSdkEnabled && checkIfAwsWafNativeSdkAvailable();

    if (canUseNativeSdk) {
      return 'native-sdk';
    }

    return 'webview';
  }, [isAwsWafEnabled, isNativeSdkEnabled]);

  return tokenSource;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type AwsWafTokenSource = 'disabled' | 'web-script' | 'native-sdk' | 'webview';
